/**
 * Onboarding Wizard — guides a new company from empty system to first invoice.
 *
 * Shows automatically on first dashboard load for the company OWNER when
 * `company.onboarding.completed` and `.skipped` are both false. Fully
 * skippable (entire wizard or per-step) and resumable.
 *
 * Steps:
 *   1. Welcome + business type
 *   2. Tax setup (enables non-tax modules if needed)
 *   3. Add first customer        (skippable)
 *   4. Add first vendor          (skippable)
 *   5. Add top products          (skippable)
 *   6. Add a bank account        (skippable)
 *   7. Finish — summary + create-first-invoice
 *
 * Data-creating steps use the same helpers as the master pages, so the
 * customer/vendor/bank get proper linked COA accounts.
 */

import React, { useState, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { doc, updateDoc, addDoc, collection } from 'firebase/firestore';
import {
  FiX, FiArrowLeft, FiArrowRight, FiCheck, FiCheckCircle, FiSkipForward,
  FiShoppingBag, FiTool, FiBriefcase, FiCoffee, FiUser, FiTruck, FiBox, FiCreditCard
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { createLinkedAccount } from '../../utils/coaInitializer';

const BUSINESS_TYPES = [
  { id: 'retail', label: 'Retail / Trading', icon: FiShoppingBag, desc: 'Buy and sell goods' },
  { id: 'manufacturing', label: 'Manufacturing', icon: FiTool, desc: 'Produce goods from materials' },
  { id: 'services', label: 'Services', icon: FiBriefcase, desc: 'Sell services, not stock' },
  { id: 'fnb', label: 'Restaurant / F&B', icon: FiCoffee, desc: 'Food & beverage' },
];

const PK_BANKS = ['HBL', 'MCB', 'United Bank (UBL)', 'Allied Bank', 'Bank Alfalah', 'Meezan Bank',
  'Faysal Bank', 'Bank Al Habib', 'National Bank (NBP)', 'Standard Chartered', 'JS Bank',
  'Askari Bank', 'Soneri Bank', 'BankIslami', 'Habib Metro'];

const STEP_KEYS = ['welcome', 'tax', 'customer', 'vendor', 'items', 'bank', 'finish'];

const OnboardingWizard = () => {
  const { activeCompany, currentUser } = useAuth();

  // ── Gate: only show for the owner of a NEW company that hasn't finished
  // onboarding. Existing companies (signed up before this feature) have no
  // `onboarding` field at all — they never see the wizard.
  const shouldShow = useMemo(() => {
    if (!activeCompany || !currentUser) return false;
    if (activeCompany.ownerId !== currentUser.uid) return false;
    const ob = activeCompany.onboarding;
    if (!ob) return false;                       // pre-existing company → skip
    return ob.completed !== true && ob.skipped !== true;
  }, [activeCompany, currentUser]);

  const [dismissed, setDismissed] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Per-step form data + "created" markers
  const [businessType, setBusinessType] = useState(activeCompany?.businessType || '');
  const [taxMode, setTaxMode] = useState('gst'); // gst | nontax | mixed
  const [customer, setCustomer] = useState({ name: '', phone: '', address: '', _createdId: null });
  const [vendor, setVendor] = useState({ name: '', phone: '', ntn: '', _createdId: null });
  const [items, setItems] = useState([
    { name: '', salePrice: '', purchasePrice: '', _created: false },
    { name: '', salePrice: '', purchasePrice: '', _created: false },
    { name: '', salePrice: '', purchasePrice: '', _created: false },
  ]);
  const [bank, setBank] = useState({ bankName: '', accountTitle: '', accountNumber: '', openingBalance: '', _createdId: null });

  const companyRef = useCallback(
    () => doc(db, 'companies', activeCompany.id),
    [activeCompany?.id]
  );

  if (!shouldShow || dismissed) return null;

  const stepKey = STEP_KEYS[stepIndex];
  const isLast = stepIndex === STEP_KEYS.length - 1;

  // ── Persist onboarding progress on the company doc
  const saveProgress = async (patch) => {
    try {
      await updateDoc(companyRef(), {
        onboarding: {
          ...(activeCompany.onboarding || {}),
          lastStep: stepKey,
          ...patch,
        },
      });
    } catch (e) { console.error('onboarding saveProgress:', e); }
  };

  // ── Skip the whole wizard
  const handleSkipAll = async () => {
    if (!window.confirm('Skip the setup wizard? You can run it again anytime from the Help menu.')) return;
    await saveProgress({ skipped: true });
    setDismissed(true);
  };

  // ── Finish — mark complete and reload so newly-enabled modules show.
  // If they set up a customer + products, drop them straight on the Sale
  // Invoice form (the "first invoice" moment). Otherwise → dashboard.
  const handleFinish = async () => {
    setSaving(true);
    try {
      await updateDoc(companyRef(), {
        businessType: businessType || activeCompany.businessType || '',
        onboarding: {
          ...(activeCompany.onboarding || {}),
          completed: true,
          completedAt: new Date().toISOString(),
          completedBy: currentUser.uid,
          businessType,
        },
      });
      setDismissed(true);
      const readyForInvoice = customer._createdId && items.some(i => i._created);
      window.location.href = readyForInvoice ? '/sales/sale-invoice' : '/dashboard';
    } catch (e) {
      setError('Could not finish setup: ' + e.message);
      setSaving(false);
    }
  };

  const goNext = () => { setError(''); setStepIndex(i => Math.min(i + 1, STEP_KEYS.length - 1)); };
  const goBack = () => { setError(''); setStepIndex(i => Math.max(i - 1, 0)); };

  // ── Step actions ───────────────────────────────────────────────────────────

  const handleTaxNext = async () => {
    setSaving(true);
    try {
      const enabled = { ...(activeCompany.enabledModules || {}) };
      if (taxMode === 'nontax' || taxMode === 'mixed') {
        enabled.sales_non_tax_invoice = true;
        enabled.sales_non_tax_return = true;
        enabled.purchases_non_tax_invoice = true;
        enabled.purchases_non_tax_return = true;
      }
      await updateDoc(companyRef(), { enabledModules: enabled });
      await saveProgress({ steps: { ...(activeCompany.onboarding?.steps || {}), tax: 'complete' } });
      goNext();
    } catch (e) {
      setError('Could not save tax setup: ' + e.message);
    } finally { setSaving(false); }
  };

  const handleCustomerNext = async () => {
    // Skippable — empty name = skip
    if (!customer.name.trim()) { goNext(); return; }
    if (customer._createdId) { goNext(); return; } // already created
    setSaving(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const docRef = await addDoc(ref, {
        companyName: customer.name.trim(),
        contactPerson: '',
        phone: customer.phone.trim(),
        email: '', address: customer.address.trim(),
        creditLimit: 0, creditDays: 30, isActive: true,
        createdAt: new Date().toISOString(), createdBy: currentUser.uid,
      });
      const acc = await createLinkedAccount(activeCompany.id, 'customer', docRef.id, customer.name.trim(), currentUser.uid);
      if (acc?.success) {
        await updateDoc(doc(ref, docRef.id), {
          linkedAccountId: acc.accountId, linkedAccountCode: acc.accountCode,
        });
      }
      setCustomer(c => ({ ...c, _createdId: docRef.id }));
      goNext();
    } catch (e) {
      setError('Could not add customer: ' + e.message);
    } finally { setSaving(false); }
  };

  const handleVendorNext = async () => {
    if (!vendor.name.trim()) { goNext(); return; }
    if (vendor._createdId) { goNext(); return; }
    setSaving(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const docRef = await addDoc(ref, {
        vendorName: vendor.name.trim(),
        contactPerson: '', phone: vendor.phone.trim(),
        ntn: vendor.ntn.trim(), email: '', address: '',
        vendorType: 'Supplier', paymentTerms: 30, isActive: true,
        createdAt: new Date().toISOString(), createdBy: currentUser.uid,
      });
      const acc = await createLinkedAccount(activeCompany.id, 'vendor', docRef.id, vendor.name.trim(), currentUser.uid);
      if (acc?.success) {
        await updateDoc(doc(ref, docRef.id), {
          linkedAccountId: acc.accountId, linkedAccountCode: acc.accountCode,
        });
      }
      setVendor(v => ({ ...v, _createdId: docRef.id }));
      goNext();
    } catch (e) {
      setError('Could not add vendor: ' + e.message);
    } finally { setSaving(false); }
  };

  const handleItemsNext = async () => {
    const toCreate = items.filter(it => it.name.trim() && !it._created);
    if (toCreate.length === 0) { goNext(); return; }
    setSaving(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      // Sequential so item codes don't collide
      let seq = Date.now();
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it.name.trim() || it._created) continue;
        seq += 1;
        const isService = businessType === 'services';
        await addDoc(ref, {
          itemCode: `ITM-${String(seq).slice(-6)}`,
          itemName: it.name.trim(),
          itemType: isService ? 'service' : 'product',
          unitOfMeasure: 'Pcs',
          salePrice: Number(it.salePrice) || 0,
          purchasePrice: Number(it.purchasePrice) || 0,
          minimumPrice: 0,
          taxable: false,
          gstRate: 18,
          trackingType: 'None',
          costingMethod: 'weightedAverage',
          openingStock: 0,
          isActive: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.uid,
        });
      }
      setItems(prev => prev.map(it => it.name.trim() ? { ...it, _created: true } : it));
      goNext();
    } catch (e) {
      setError('Could not add products: ' + e.message);
    } finally { setSaving(false); }
  };

  const handleBankNext = async () => {
    if (!bank.bankName.trim()) { goNext(); return; }
    if (bank._createdId) { goNext(); return; }
    setSaving(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'banks');
      const ob = Number(bank.openingBalance) || 0;
      const docRef = await addDoc(ref, {
        bankName: bank.bankName.trim(),
        branchName: '',
        accountTitle: bank.accountTitle.trim(),
        accountNumber: bank.accountNumber.trim(),
        accountType: 'Current',
        openingBalance: ob, currentBalance: ob,
        isDefault: true, isActive: true,
        createdAt: new Date().toISOString(), createdBy: currentUser.uid,
      });
      const acc = await createLinkedAccount(activeCompany.id, 'bank', docRef.id, bank.bankName.trim(), currentUser.uid);
      if (acc?.success) {
        await updateDoc(doc(ref, docRef.id), {
          linkedAccountId: acc.accountId, linkedAccountCode: acc.accountCode,
        });
      }
      setBank(b => ({ ...b, _createdId: docRef.id }));
      goNext();
    } catch (e) {
      setError('Could not add bank: ' + e.message);
    } finally { setSaving(false); }
  };

  // Router for the primary action button
  const primaryAction = () => {
    if (saving) return;
    switch (stepKey) {
      case 'welcome':  if (!businessType) { setError('Please pick what your business does.'); return; } goNext(); break;
      case 'tax':      handleTaxNext(); break;
      case 'customer': handleCustomerNext(); break;
      case 'vendor':   handleVendorNext(); break;
      case 'items':    handleItemsNext(); break;
      case 'bank':     handleBankNext(); break;
      case 'finish':   handleFinish(); break;
      default: goNext();
    }
  };

  const skippableSteps = ['customer', 'vendor', 'items', 'bank'];
  const canSkipStep = skippableSteps.includes(stepKey);

  // ── Render each step ───────────────────────────────────────────────────────
  const renderStep = () => {
    switch (stepKey) {
      case 'welcome':
        return (
          <>
            <StepTitle>Welcome to AMAL ERP 👋</StepTitle>
            <StepDesc>Let's get your business set up. This takes about 8 minutes — and you can skip any step.</StepDesc>
            <FieldLabel>What does your business do?</FieldLabel>
            <TileGrid>
              {BUSINESS_TYPES.map(bt => (
                <Tile key={bt.id} $active={businessType === bt.id} onClick={() => setBusinessType(bt.id)}>
                  <bt.icon size={26} />
                  <TileLabel>{bt.label}</TileLabel>
                  <TileDesc>{bt.desc}</TileDesc>
                </Tile>
              ))}
            </TileGrid>
          </>
        );

      case 'tax':
        return (
          <>
            <StepTitle>How do you handle sales tax?</StepTitle>
            <StepDesc>This decides whether your invoices carry GST. You can change it later.</StepDesc>
            <RadioCard $active={taxMode === 'gst'} onClick={() => setTaxMode('gst')}>
              <RadioDot $active={taxMode === 'gst'} />
              <div>
                <RadioTitle>GST-Registered</RadioTitle>
                <RadioDesc>Issue tax invoices with GST. Standard for registered businesses.</RadioDesc>
              </div>
            </RadioCard>
            <RadioCard $active={taxMode === 'nontax'} onClick={() => setTaxMode('nontax')}>
              <RadioDot $active={taxMode === 'nontax'} />
              <div>
                <RadioTitle>Non-Tax (Cash Business)</RadioTitle>
                <RadioDesc>No GST on invoices. Enables non-tax invoice documents.</RadioDesc>
              </div>
            </RadioCard>
            <RadioCard $active={taxMode === 'mixed'} onClick={() => setTaxMode('mixed')}>
              <RadioDot $active={taxMode === 'mixed'} />
              <div>
                <RadioTitle>Both (Mixed)</RadioTitle>
                <RadioDesc>Some sales with GST, some without. Enables both document types.</RadioDesc>
              </div>
            </RadioCard>
          </>
        );

      case 'customer':
        return (
          <>
            <StepTitle><FiUser /> Add your first customer</StepTitle>
            <StepDesc>Add the customer you deal with most. You can skip this and add customers later.</StepDesc>
            {customer._createdId ? (
              <CreatedBox><FiCheckCircle /> Customer <b>{customer.name}</b> added.</CreatedBox>
            ) : (
              <>
                <Field><FieldLabel>Customer Name</FieldLabel>
                  <Input value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value })} placeholder="e.g. ABC Trading" /></Field>
                <Field><FieldLabel>Phone (WhatsApp)</FieldLabel>
                  <Input value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} placeholder="03xx xxxxxxx" /></Field>
                <Field><FieldLabel>Address</FieldLabel>
                  <Input value={customer.address} onChange={e => setCustomer({ ...customer, address: e.target.value })} placeholder="City, area" /></Field>
              </>
            )}
          </>
        );

      case 'vendor':
        return (
          <>
            <StepTitle><FiTruck /> Add your first vendor</StepTitle>
            <StepDesc>Add a supplier you buy from. Skip if you don't need it yet.</StepDesc>
            {vendor._createdId ? (
              <CreatedBox><FiCheckCircle /> Vendor <b>{vendor.name}</b> added.</CreatedBox>
            ) : (
              <>
                <Field><FieldLabel>Vendor Name</FieldLabel>
                  <Input value={vendor.name} onChange={e => setVendor({ ...vendor, name: e.target.value })} placeholder="e.g. XYZ Suppliers" /></Field>
                <Field><FieldLabel>Phone</FieldLabel>
                  <Input value={vendor.phone} onChange={e => setVendor({ ...vendor, phone: e.target.value })} placeholder="03xx xxxxxxx" /></Field>
                <Field><FieldLabel>NTN (optional)</FieldLabel>
                  <Input value={vendor.ntn} onChange={e => setVendor({ ...vendor, ntn: e.target.value })} placeholder="Tax number" /></Field>
              </>
            )}
          </>
        );

      case 'items':
        return (
          <>
            <StepTitle><FiBox /> Add your {businessType === 'services' ? 'services' : 'products'}</StepTitle>
            <StepDesc>Add up to 3 to get started. {businessType === 'services' ? '' : 'Leave purchase price blank if unsure.'}</StepDesc>
            <ItemHeaderRow>
              <span style={{ flex: 2 }}>Name</span>
              <span style={{ flex: 1 }}>Sale Price</span>
              {businessType !== 'services' && <span style={{ flex: 1 }}>Purchase Price</span>}
            </ItemHeaderRow>
            {items.map((it, idx) => (
              <ItemRow key={idx}>
                <Input style={{ flex: 2 }} value={it.name} disabled={it._created}
                  onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                  placeholder={`${businessType === 'services' ? 'Service' : 'Product'} ${idx + 1}`} />
                <Input style={{ flex: 1 }} type="number" value={it.salePrice} disabled={it._created}
                  onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, salePrice: e.target.value } : x))}
                  placeholder="0" />
                {businessType !== 'services' && (
                  <Input style={{ flex: 1 }} type="number" value={it.purchasePrice} disabled={it._created}
                    onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, purchasePrice: e.target.value } : x))}
                    placeholder="0" />
                )}
                {it._created && <CreatedTick><FiCheck /></CreatedTick>}
              </ItemRow>
            ))}
          </>
        );

      case 'bank':
        return (
          <>
            <StepTitle><FiCreditCard /> Add a bank account</StepTitle>
            <StepDesc>Add your main business bank account with its current balance. Skippable.</StepDesc>
            {bank._createdId ? (
              <CreatedBox><FiCheckCircle /> Bank <b>{bank.bankName}</b> added.</CreatedBox>
            ) : (
              <>
                <Field><FieldLabel>Bank</FieldLabel>
                  <Input list="pk-banks" value={bank.bankName}
                    onChange={e => setBank({ ...bank, bankName: e.target.value })} placeholder="Start typing…" />
                  <datalist id="pk-banks">{PK_BANKS.map(b => <option key={b} value={b} />)}</datalist>
                </Field>
                <Field><FieldLabel>Account Title</FieldLabel>
                  <Input value={bank.accountTitle} onChange={e => setBank({ ...bank, accountTitle: e.target.value })} placeholder="Account holder name" /></Field>
                <Field><FieldLabel>Account Number</FieldLabel>
                  <Input value={bank.accountNumber} onChange={e => setBank({ ...bank, accountNumber: e.target.value })} placeholder="Account #" /></Field>
                <Field><FieldLabel>Opening Balance (Rs.)</FieldLabel>
                  <Input type="number" value={bank.openingBalance} onChange={e => setBank({ ...bank, openingBalance: e.target.value })} placeholder="0" /></Field>
              </>
            )}
          </>
        );

      case 'finish':
        return (
          <>
            <StepTitle>You're all set! 🎉</StepTitle>
            <StepDesc>Your AMAL ERP is ready. Here's what you've done:</StepDesc>
            <SummaryList>
              <SummaryItem $done><FiCheckCircle /> Business profile &amp; tax setup</SummaryItem>
              <SummaryItem $done={!!customer._createdId}>
                <FiCheckCircle /> {customer._createdId ? `Customer: ${customer.name}` : 'Customer — skipped'}
              </SummaryItem>
              <SummaryItem $done={!!vendor._createdId}>
                <FiCheckCircle /> {vendor._createdId ? `Vendor: ${vendor.name}` : 'Vendor — skipped'}
              </SummaryItem>
              <SummaryItem $done={items.some(i => i._created)}>
                <FiCheckCircle /> {items.some(i => i._created) ? `${items.filter(i => i._created).length} product(s) added` : 'Products — skipped'}
              </SummaryItem>
              <SummaryItem $done={!!bank._createdId}>
                <FiCheckCircle /> {bank._createdId ? `Bank: ${bank.bankName}` : 'Bank — skipped'}
              </SummaryItem>
            </SummaryList>
            <StepDesc style={{ marginTop: 16 }}>
              Next: create your first Sale Invoice. Your customer and products are ready to pick.
            </StepDesc>
          </>
        );

      default: return null;
    }
  };

  const primaryLabel = () => {
    if (saving) return 'Saving…';
    if (stepKey === 'finish') {
      return (customer._createdId && items.some(i => i._created))
        ? 'Create First Invoice'
        : 'Go to Dashboard';
    }
    if (canSkipStep) return 'Save & Continue';
    return 'Continue';
  };

  return (
    <Overlay>
      <Modal>
        <Header>
          <Brand>AMAL ERP &middot; Setup</Brand>
          <HeaderRight>
            {!isLast && <SkipAllBtn onClick={handleSkipAll}>Skip setup</SkipAllBtn>}
            <CloseBtn onClick={handleSkipAll} title="Close"><FiX /></CloseBtn>
          </HeaderRight>
        </Header>

        <ProgressWrap>
          <ProgressText>Step {stepIndex + 1} of {STEP_KEYS.length}</ProgressText>
          <Dots>
            {STEP_KEYS.map((k, i) => (
              <Dot key={k} $active={i === stepIndex} $done={i < stepIndex} />
            ))}
          </Dots>
        </ProgressWrap>

        <Body>
          {renderStep()}
          {error && <ErrorMsg>{error}</ErrorMsg>}
        </Body>

        <Footer>
          <BackBtn onClick={goBack} disabled={stepIndex === 0 || saving}>
            <FiArrowLeft /> Back
          </BackBtn>
          <FooterRight>
            {canSkipStep && (
              <SkipStepBtn onClick={goNext} disabled={saving}>
                <FiSkipForward /> Skip this step
              </SkipStepBtn>
            )}
            <PrimaryBtn onClick={primaryAction} disabled={saving}>
              {primaryLabel()} {!saving && stepKey !== 'finish' && <FiArrowRight />}
              {!saving && stepKey === 'finish' && <FiCheck />}
            </PrimaryBtn>
          </FooterRight>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default OnboardingWizard;

// ── Styled ───────────────────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 9000;
  background: rgba(20, 0, 8, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;
const Modal = styled.div`
  background: #fff; border-radius: 16px; width: 100%; max-width: 620px;
  max-height: 92vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 60px rgba(0,0,0,0.35);
  animation: ${fadeIn} 0.25s ease;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; background: ${colors.primary?.main || '#800020'}; color: #fff;
`;
const Brand = styled.div`font-weight: 700; font-size: 15px; letter-spacing: 0.3px;`;
const HeaderRight = styled.div`display: flex; align-items: center; gap: 8px;`;
const SkipAllBtn = styled.button`
  background: transparent; border: 1px solid rgba(255,255,255,0.4); color: #fff;
  font-size: 12px; padding: 5px 12px; border-radius: 14px; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.12); }
`;
const CloseBtn = styled.button`
  background: transparent; border: none; color: #fff; cursor: pointer;
  display: flex; padding: 4px; border-radius: 6px;
  &:hover { background: rgba(255,255,255,0.15); }
`;
const ProgressWrap = styled.div`padding: 14px 22px 0;`;
const ProgressText = styled.div`font-size: 12px; color: ${colors.text?.secondary || '#777'}; margin-bottom: 6px;`;
const Dots = styled.div`display: flex; gap: 6px;`;
const Dot = styled.div`
  flex: 1; height: 5px; border-radius: 3px;
  background: ${p => p.$active ? (colors.primary?.main || '#800020')
    : p.$done ? '#C8A2AE' : '#eee'};
  transition: background 0.3s;
`;
const Body = styled.div`padding: 22px; overflow-y: auto; flex: 1;`;
const StepTitle = styled.h2`
  margin: 0 0 6px; font-size: 21px; font-weight: 700; color: ${colors.text?.primary || '#1a1a1a'};
  display: flex; align-items: center; gap: 8px;
`;
const StepDesc = styled.p`margin: 0 0 18px; font-size: 14px; color: ${colors.text?.secondary || '#777'}; line-height: 1.55;`;
const FieldLabel = styled.label`display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; color: ${colors.text?.secondary || '#777'}; margin-bottom: 5px;`;
const Field = styled.div`margin-bottom: 14px;`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 8px;
  font-size: 14px; font-family: inherit;
  &:focus { outline: none; border-color: ${colors.primary?.main || '#800020'}; }
  &:disabled { background: #f5f5f5; color: #888; }
`;
const TileGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 12px;`;
const Tile = styled.button`
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  padding: 16px; border-radius: 12px; cursor: pointer; text-align: left;
  background: ${p => p.$active ? (colors.primary?.main || '#800020') + '0d' : '#fafafa'};
  border: 2px solid ${p => p.$active ? (colors.primary?.main || '#800020') : '#eee'};
  color: ${p => p.$active ? (colors.primary?.main || '#800020') : (colors.text?.primary || '#333')};
  &:hover { border-color: ${colors.primary?.main || '#800020'}; }
`;
const TileLabel = styled.div`font-weight: 700; font-size: 14px;`;
const TileDesc = styled.div`font-size: 12px; color: ${colors.text?.secondary || '#888'};`;
const RadioCard = styled.div`
  display: flex; align-items: flex-start; gap: 12px; padding: 14px;
  border-radius: 10px; cursor: pointer; margin-bottom: 10px;
  background: ${p => p.$active ? (colors.primary?.main || '#800020') + '0d' : '#fafafa'};
  border: 2px solid ${p => p.$active ? (colors.primary?.main || '#800020') : '#eee'};
`;
const RadioDot = styled.div`
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;
  border: 2px solid ${p => p.$active ? (colors.primary?.main || '#800020') : '#bbb'};
  background: ${p => p.$active
    ? `radial-gradient(circle, ${colors.primary?.main || '#800020'} 0 5px, transparent 6px)`
    : 'transparent'};
`;
const RadioTitle = styled.div`font-weight: 700; font-size: 14px;`;
const RadioDesc = styled.div`font-size: 12.5px; color: ${colors.text?.secondary || '#888'}; margin-top: 2px;`;
const ItemHeaderRow = styled.div`
  display: flex; gap: 8px; font-size: 11px; font-weight: 600; text-transform: uppercase;
  color: ${colors.text?.secondary || '#999'}; margin-bottom: 6px; padding: 0 2px;
`;
const ItemRow = styled.div`display: flex; gap: 8px; margin-bottom: 8px; align-items: center;`;
const CreatedTick = styled.span`color: #2e7d32; display: flex;`;
const CreatedBox = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 14px;
  background: #e8f5e9; color: #1b5e20; border-radius: 8px; font-size: 14px;
`;
const SummaryList = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const SummaryItem = styled.div`
  display: flex; align-items: center; gap: 8px; font-size: 14px;
  color: ${p => p.$done ? '#1b5e20' : '#999'};
  svg { color: ${p => p.$done ? '#2e7d32' : '#ccc'}; flex-shrink: 0; }
`;
const ErrorMsg = styled.div`
  margin-top: 14px; padding: 10px 12px; background: #fdecea; color: #b00020;
  border-radius: 8px; font-size: 13px;
`;
const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 22px; border-top: 1px solid #eee; background: #fafafa;
`;
const FooterRight = styled.div`display: flex; align-items: center; gap: 10px;`;
const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent; border: none; color: ${colors.text?.secondary || '#777'};
  font-size: 14px; cursor: pointer; padding: 8px;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const SkipStepBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent; border: 1px solid #ccc; color: ${colors.text?.secondary || '#777'};
  font-size: 13px; padding: 8px 14px; border-radius: 8px; cursor: pointer;
  &:hover { background: #f0f0f0; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const PrimaryBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${colors.primary?.main || '#800020'}; color: #fff; border: none;
  font-size: 14px; font-weight: 600; padding: 10px 20px; border-radius: 8px; cursor: pointer;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
